import { useIsMobile, useLazyRef } from '../shared/lib/hooks.ts'
import { cn } from '../shared/lib/utils.ts'
import { useCallback, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { getLessonIconByTypeOrIndex } from '../shared/data/programs.ts'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { urls } from '../shared/urls.ts'
import { useAppModule } from '../features/appContext.ts'
import {
  SpeakingStoreContext,
  useSpeakingStore,
  type SpeakingLesson,
  type SpeakingLessonUnit,
  type SpeakingStore,
} from '../features/speaking/speakingStore.ts'
import { useSnapshot } from 'valtio/react'
import { UnitDashedLine } from '../shared/ui/unitDashedLine.tsx'
import { Spinner } from '../shared/ui/spinner.tsx'
import { LinkButton } from '../shared/ui/button/button.tsx'
import { motion } from 'framer-motion'
import { TopicCard } from './programPage2.tsx'
import { Icon } from '../shared/ui/icon/icon.tsx'

function isSpeakingSimulatorLocked(
  accountLoading: boolean,
  psychologyLessonsTotal: number | undefined,
  psychologyLessonsCompleted: number | undefined,
  isPsychologySpeakingAvailable: boolean | undefined,
) {
  if (
    accountLoading ||
    !isPsychologySpeakingAvailable ||
    psychologyLessonsTotal !== psychologyLessonsCompleted
  )
    return true
  return false
}

export function SpeakingPage() {
  const appModule = useAppModule()
  const store = useLazyRef(() => appModule.speakingStore()).current
  const authState = useSnapshot(appModule.authStore.state)
  const [searchParams] = useSearchParams()
  const userId =
    searchParams.get('user_id') ?? authState.user?.user_id?.toString() ?? ''
  const speakingState = useSnapshot(store.state)
  const selectedTopic = speakingState.topics.find(
    (topic) => topic.title === speakingState.selectedTopicTitle,
  )

  const currentCourse = appModule.appStorage.get('currentCourse')
  const isPsychologyCourse = currentCourse?.slug?.toLowerCase() === 'psychology'
  const courseId = currentCourse?.id ?? undefined

  const simulatorLocked =
    isPsychologyCourse &&
    isSpeakingSimulatorLocked(
      speakingState.accountLoading,
      speakingState.psychologyLessonsTotal,
      speakingState.psychologyLessonsCompleted,
      speakingState.isPsychologySpeakingAvailable,
    )

  useEffect(() => {
    if (isPsychologyCourse) void store.loadAccount()
  }, [store, isPsychologyCourse])

  useEffect(() => {
    void store.loadLessons(courseId)
  }, [store, courseId])

  if (speakingState.lessonsLoading)
    return (
      <div className="flex h-screen  w-full items-center justify-center">
        <Spinner />
      </div>
    )

  return (
    <SpeakingStoreContext.Provider value={store}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex w-full flex-col gap-18 overflow-hidden bg-white msm:pt-0 mxl:pt-16 xl:flex-row xl:bg-bglight xl:px-40 xl:py-24"
      >
        <div className="mxl:contents xl:m-0 xl:flex xl:w-[calc(100%-300px)] xl:flex-col xl:gap-16">
          {simulatorLocked ? (
            <div className="mx-auto flex w-full flex-col gap-16 rounded-18 bg-white p-16 text-16 leading-normal text-black mxl:order-1 xl:mx-0 xl:rounded-24 xl:p-24 xl:shadow-drop">
              <p>
                <FormattedMessage id="speaking.simulatorLockedNotice" />
              </p>
              {typeof speakingState.psychologyLessonsCompleted === 'number' &&
              typeof speakingState.psychologyLessonsTotal === 'number' ? (
                <p>
                  <FormattedMessage
                    id="speaking.simulatorLockedProgress"
                    values={{
                      completed: speakingState.psychologyLessonsCompleted,
                      total: speakingState.psychologyLessonsTotal,
                    }}
                  />
                </p>
              ) : null}
              <LinkButton
                to={urls.learningProfile()}
                bg="blue-gradient"
                size="medium"
                rounded="full"
                className="w-full max-w-full sm:w-auto"
              >
                <FormattedMessage id="speaking.toLessonsButton" />
              </LinkButton>
            </div>
          ) : null}
          <motion.div
            className={cn(
              'mx-auto flex w-full flex-col xl:mb-0',
              simulatorLocked ? 'mxl:order-3' : 'mxl:order-1',
            )}
          >
            <SpeakingTopicsList
              store={store}
              simulatorLocked={simulatorLocked}
            />
            <Units
              title={selectedTopic?.title ?? ''}
              units={selectedTopic?.units ?? []}
              userId={userId}
              simulatorLocked={simulatorLocked}
            />
          </motion.div>
        </div>
        <div className="mxl:order-2 xl:contents">
          <TimerBlock simulatorLocked={simulatorLocked} />
        </div>
      </motion.div>
    </SpeakingStoreContext.Provider>
  )
}

function SpeakingTopicsList({
  store,
  simulatorLocked,
}: {
  store: SpeakingStore
  simulatorLocked: boolean
}) {
  const state = useSnapshot(store.state)

  return (
    <div
      className={cn(
        'mb-16 flex flex-col gap-12 bg-white px-16 pb-16 xl:mb-0 xl:w-full xl:gap-0 xl:rounded-24 xl:p-0 xl:shadow-drop',
        simulatorLocked && 'pointer-events-none blur-sm',
      )}
      id="speaking-topic-select"
    >
      <div className="hidden text-16 font-extrabold text-black xl:flex xl:items-center xl:justify-between xl:p-16 xl:pb-0">
        <FormattedMessage id="chooseTopic" />
        <Icon iconName="arrowRight" className="text-light" size="sm" />
      </div>
      <div
        className={cn(
          'no-scrollbar flex gap-16 overflow-auto xl:gap-12 xl:p-16 xl:pt-12 xl:[scrollbar-width:thin]',
        )}
      >
        {state.topics.map((topic, i) => (
          <TopicCard
            key={topic.title}
            index={i + 1}
            lessonsAmount={topic.units.reduce(
              (acc, unit) => acc + unit.lessons.length,
              0,
            )}
            isActive={state.selectedTopicTitle === topic.title}
            title={topic.title}
            onClick={() => {
              store.setSelectedTopic(topic.title)
            }}
          />
        ))}
      </div>
    </div>
  )
}

interface UnitsProps {
  title: string
  units: readonly SpeakingLessonUnit[]
  userId: string
  simulatorLocked: boolean
}

function Units({ title, units, userId, simulatorLocked }: UnitsProps) {
  const lessons = units.flatMap((unit) => unit.lessons)
  return (
    <div
      className={cn(
        'mx-auto mb-100 flex w-full flex-col gap-16 bg-white p-16 xl:m-0 xl:mt-16 xl:rounded-24 xl:p-24 xl:shadow-drop',
        simulatorLocked && 'pointer-events-none blur-sm',
      )}
    >
      <UnitHeader title={title} id="speaking-unit" />
      <div className="flex h-full">
        <UnitDashedLine id="speaking-unit" />
        <div
          className="relative flex h-fit w-full flex-wrap gap-16 msm:flex-col xl:ml-20 xl:mt-18 xl:flex-col xl:flex-nowrap"
          id="speaking-unit-lessons"
        >
          {lessons.map((lesson, index) => (
            <SpeakingLessonCard
              key={lesson.tag}
              lesson={{ ...lesson, order: index + 1 }}
              userId={userId}
              index={index}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface UnitHeaderProps {
  title: string
  id: string
}

export const TimerBlock = ({
  simulatorLocked,
}: {
  simulatorLocked: boolean
}) => {
  const store = useSpeakingStore()
  const state = useSnapshot(store.state)

  const formatTime = useCallback((secs: number | null) => {
    if (secs == null) return '--:--'
    const clamped = Math.max(0, Math.floor(secs))
    const mins = Math.floor(clamped / 60)
    const sec = clamped % 60
    return `${mins.toString().padStart(2, '0')}:${sec
      .toString()
      .padStart(2, '0')}`
  }, [])

  useEffect(() => {
    void store.loadVoiceLimit()
  }, [store])

  return (
    <div
      className={cn(
        'mx-16 mb-16 flex w-auto flex-col items-center rounded-18 bg-white p-20 text-center text-black shadow-drop xl:mx-0 xl:mb-0 xl:w-[282px] xl:rounded-24 xl:p-32 xl:h-fit xl:select-none',
        simulatorLocked && 'blur-sm',
      )}
    >
      <p className="text-14 font-extrabold xl:text-18">
        <FormattedMessage id="improveSpeaking" />
      </p>
      <p className="mt-16 text-14 font-extrabold xl:mt-32 xl:text-18">
        <FormattedMessage id="speakingTimeLeft" />
      </p>
      <p className="mt-4 text-24 font-extrabold xl:text-[32px]">
        {state.loading ? '…' : formatTime(state.remainingSeconds)}
      </p>
    </div>
  )
}

function UnitHeader({ title, id }: UnitHeaderProps) {
  const module = useAppModule()
  const isMobile = useIsMobile()
  const appState = useSnapshot(module.appStore.state)
  const isRu = appState.uiLocale === 'ru'
  return (
    <div className="px-16 xl:px-0" id={`${id}-header`}>
      <div className="text-24 font-extrabold text-black xl:text-18 xl:leading-none">
        {isRu ? (
          <FormattedMessage id="navbar.lessons" />
        ) : isMobile ? (
          <FormattedMessage id="unit" />
        ) : (
          <FormattedMessage id="unitDesktop" values={{ title }} />
        )}
      </div>

      <div className="overflow-hidden text-ellipsis text-nowrap text-18 font-extrabold leading-none text-black xl:hidden">
        {title}
      </div>
    </div>
  )
}

interface SpeakingLessonCardProps {
  lesson: SpeakingLesson
  userId: string
  index: number
}

const pseudoNumber =
  'relative xl:before:content-[attr(data-index)] xl:before:absolute xl:before:-left-[36px] xl:before:top-0 xl:before:flex xl:before:items-center xl:before:justify-center xl:before:size-[28px] xl:before:rounded-full xl:before:bg-gray-light xl:before:text-14 xl:before:font-extrabold xl:before:text-purple3'

const REALTIME_HARDCODED_TAG = 'rp3'

function SpeakingLessonCard({
  lesson,
  userId,
  index,
}: SpeakingLessonCardProps) {
  const navigate = useNavigate()
  const appModule = useAppModule()
  const alwaysNotStarted = lesson.tag === REALTIME_HARDCODED_TAG
  const isCompleted = !alwaysNotStarted && lesson.status === 'completed'
  const displayStatus = alwaysNotStarted ? 'new' : lesson.status

  const handleClickRTLesson = () => {
    const params = new URLSearchParams()
    const tag = lesson.tag === REALTIME_HARDCODED_TAG ? 'rp3' : lesson.tag
    params.set('program_tag', tag)
    params.set('lesson_id', tag)
    if (userId) {
      params.set('user_id', userId)
    }
    navigate(`${urls.liveSession}?${params.toString()}`)
  }

  return (
    <div
      className={cn(
        'relative flex w-full items-baseline rounded-18 bg-gray-light p-16',
        pseudoNumber,
        isCompleted ? 'cursor-default opacity-80' : 'cursor-pointer',
      )}
      id={lesson.tag}
      data-index={lesson.order}
      onClick={isCompleted ? undefined : handleClickRTLesson}
      aria-disabled={isCompleted}
    >
      <div className="flex w-full items-center gap-12">
        <LessonIcon type={lesson.type} index={index} status={displayStatus} />
        <div className="text-start">
          <div className="text-14 font-extrabold text-black opacity-30">
            <FormattedMessage id="Lesson" /> {lesson.order}
          </div>

          <div className="mt-2 line-clamp-2 overflow-hidden text-ellipsis break-words text-18 font-bold leading-5 text-black">
            {lesson.title}
          </div>
        </div>
      </div>
    </div>
  )
}

const LessonIcon = ({
  type,
  index,
  status,
}: {
  type: string
  index: number
  status: SpeakingLesson['status']
}) => {
  const isCompleted = status === 'completed'
  const isActive = status === 'active'
  const iconPath = getLessonIconByTypeOrIndex(type, index)

  if (isActive) {
    return (
      <div className="relative flex size-80 min-h-80 min-w-80 items-center justify-center">
        <img src="/images/progress.svg" className="size-full" />
        <div
          className={cn(
            'absolute flex size-60 items-center justify-center overflow-hidden rounded-full',
            isCompleted ? 'bg-green-40' : 'bg-white',
          )}
        >
          <img className="w-2/3" src={iconPath} alt="" />
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <div
        className={cn(
          'relative flex size-80 min-h-80 min-w-80 shrink-0 items-center justify-center rounded-full border-4 p-6',
          isCompleted
            ? 'border-green bg-green-40'
            : 'border-lesson-icon-border',
        )}
      >
        <div
          className={cn(
            'flex size-full items-center justify-center overflow-hidden rounded-full',
            isCompleted ? 'bg-green-40' : 'bg-white',
          )}
        >
          <img className="w-2/3" src={iconPath} alt="" />
        </div>
        {isCompleted && (
          <div className="absolute -bottom-2 -left-2 flex size-24 items-center justify-center rounded-full border-2 border-white bg-green">
            <svg
              width="10"
              height="8"
              viewBox="0 0 10 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 4.50033L2.48217 6.53037C2.94672 7.16663 3.90024 7.15427 4.34816 6.50616L8.15346 1"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  )
}
